import { Box } from '@mui/material';
import { LayoutType } from 'model/deals.model';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { DealsFilters } from './components/DealsFilters';
import { DealsStatistics } from './components/DealsStatistics';
import { DealsTable } from './components/DealsTable';
import MultiPipelineKanban from './components/MultiPipelineKanban';
import { useDealsFilters } from './useDealsFilters';

type DealsMainProps = {
  clientScope?: {
    clientId?: string;
    clientName?: string;
  };
};

const DealsMain = ({ clientScope }: DealsMainProps) => {
  const currentRoleViewType =
    (sessionStorage.getItem('currentRoleViewType') as LayoutType) || LayoutType.KANBAN;
  const [layout, setLayout] = useState<LayoutType>(currentRoleViewType);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [needsAttention, setNeedsAttention] = useState(false);

  const { filterState, updateFilter } = useDealsFilters();
  const isClientScoped = Boolean(clientScope?.clientId || clientScope?.clientName);

  const lockedClientFilter = useMemo(() => {
    if (!clientScope?.clientName) return null;
    return {
      Id: clientScope.clientId || clientScope.clientName,
      Name: clientScope.clientName,
    };
  }, [clientScope?.clientId, clientScope?.clientName]);

  useEffect(() => {
    if (isClientScoped) {
      setTabValue(1);
    }
  }, [isClientScoped]);

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('currentRoleViewType', newValue);
    setLayout(newValue);
  };

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSetCurrentPage = (number: number) => {
    setCurrentPage(number);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '24px',
        gap: '16px',
      }}
    >
      {!isClientScoped && <DealsStatistics mine={tabValue === 0} />}
      <DealsFilters
        onLayoutChange={handleLayoutChange}
        layout={layout}
        filterState={filterState}
        onFilterChange={updateFilter}
        tabValue={tabValue}
        onTabChange={handleTabChange}
        onSearchChange={setSearchTerm}
        needsAttention={needsAttention}
        onNeedsAttentionChange={setNeedsAttention}
        handleSetCurrentPage={handleSetCurrentPage}
        showTabs={!isClientScoped}
        isOwnerFilterVisible={isClientScoped ? true : Boolean(tabValue)}
        hideClientFilter={isClientScoped}
        lockedClient={lockedClientFilter}
        newDealState={
          isClientScoped && lockedClientFilter
            ? {
                presetClient: {
                  id: lockedClientFilter.Id,
                  name: lockedClientFilter.Name,
                },
                lockClient: true,
              }
            : undefined
        }
      />
      {layout === LayoutType.KANBAN ? (
        <MultiPipelineKanban
          filterState={filterState}
          tabValue={tabValue}
          searchTerm={searchTerm}
          needsAttention={needsAttention}
          mineOverride={isClientScoped ? false : undefined}
          clientNameOverride={lockedClientFilter?.Name}
          subSections={isClientScoped}
        />
      ) : (
        <DealsTable
          filterState={filterState}
          tabValue={tabValue}
          searchTerm={searchTerm}
          needsAttention={needsAttention}
          currentPage={currentPage}
          handleSetCurrentPage={handleSetCurrentPage}
          mineOverride={isClientScoped ? false : undefined}
          clientNameOverride={lockedClientFilter?.Name}
        />
      )}
    </Box>
  );
};

export default DealsMain;
